/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import type { ShopperSearch } from '@/scapi';
import { ProductGrid } from '@/components/product-grid';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/spinner';
import { useCategoryLoadMore } from '@/hooks/use-category-load-more';

export interface CategoryLoadMoreProps {
    categoryId: string;
    initialOffset: number;
    total: number;
    refine: string[];
    sort?: string;
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    topCategoryName?: string;
}

/**
 * Renders additional category search result pages accumulated client-side, plus the
 * "See More Products" button that triggers the next fetch. Sits below the initial
 * `DeferredProductGrid` page and appends further `ProductGrid` chunks as they load.
 */
export default function CategoryLoadMore({
    categoryId,
    initialOffset,
    total,
    refine,
    sort,
    hasRefinementsPanel,
    handleProductClick,
    topCategoryName,
}: CategoryLoadMoreProps) {
    const { t } = useTranslation('category');
    const { hits, loadMore, isLoading, hasMore } = useCategoryLoadMore({
        categoryId,
        initialOffset,
        total,
        refine,
        sort,
    });

    if (!hasMore && hits.length === 0) {
        return null;
    }

    return (
        <div data-testid="category-load-more">
            {hits.length > 0 && (
                <ProductGrid
                    critical={hits}
                    hasRefinementsPanel={hasRefinementsPanel}
                    handleProductClick={handleProductClick}
                    topCategoryName={topCategoryName}
                />
            )}

            {hasMore && (
                <div className="mt-10 flex justify-center">
                    <Button
                        variant="outline"
                        size="lg"
                        className="min-w-64 cursor-pointer"
                        onClick={loadMore}
                        disabled={isLoading}
                        aria-busy={isLoading}>
                        {isLoading && <Spinner size="sm" className="mr-2" />}
                        {isLoading ? t('loadMore.loading') : t('loadMore.cta')}
                    </Button>
                </div>
            )}
        </div>
    );
}
